import React, { useState } from 'react'
import { WheelOption } from '../types'
import { useAuthStore } from '../stores/authStore'
import { useAIUsageStore } from '../stores/aiUsageStore'
import LoginPromptModal from './LoginPromptModal'

interface ContextInputModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (context: string) => void
  result: WheelOption | null
  mode: 'normal' | 'elimination' | 'accumulation'
  options: WheelOption[]
  accumulationCounts?: Record<string, number>
}

export const ContextInputModal: React.FC<ContextInputModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  result,
  mode,
  options,
  accumulationCounts
}) => {
  const [context, setContext] = useState('')
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)
  
  const { user } = useAuthStore()
  const { canUseAI, incrementUsage, getRemainingUsage } = useAIUsageStore()

  const getAccumulationStats = () => {
    if (!accumulationCounts) return ''
    const total = Object.values(accumulationCounts).reduce((a, b) => a + b, 0)
    return options.map(o => {
      const count = accumulationCounts[o.id] || 0
      const percent = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0'
      return `${o.text}: ${count} (${percent}%)`
    }).join(', ')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (context.trim()) {
      // 检查用户登录状态和使用次数
      if (!user && !canUseAI()) {
        setShowLoginPrompt(true)
        return
      }

      // 未登录用户增加使用次数
      if (!user) {
        incrementUsage()
      }

      let aiContext = context.trim()
      if (mode === 'accumulation' && accumulationCounts) {
        aiContext = `Accumulation stats: ${getAccumulationStats()}\n\n${aiContext}`
      }
      onConfirm(aiContext)
      setContext('')
    }
  }

  const handleClose = () => {
    setContext('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              🎯 Add Decision Context for AI Analysis
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
              {mode === 'normal' ? (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Decision Result: <span className="text-blue-600">{result?.text}</span>
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Provide background information about your decision to help AI deliver better analysis and recommendations.
                  </p>
                </>
              ) : mode === 'elimination' ? (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Elimination Mode - Remaining Options
                  </h3>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {options.length > 0 ? options.map(o => (
                      <span key={o.id} className="inline-block bg-blue-100 text-blue-800 font-semibold px-4 py-2 rounded-full shadow-sm text-base">
                        {o.text}
                      </span>
                    )) : <span className="text-gray-400">No options left</span>}
                  </div>
                  <p className="text-gray-600 text-sm">
                    Provide more context about your decision and let AI help you analyze the remaining options.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Accumulation Mode - Statistics
                  </h3>
                  <div className="mb-2 text-base text-primary-700 font-medium">
                    {getAccumulationStats()}
                  </div>
                  <p className="text-gray-600 text-sm">
                    Provide more context about your decision and let AI help you analyze the accumulated results.
                  </p>
                </>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="context" className="block text-sm font-medium text-gray-700 mb-2">
                Decision Context and Background
              </label>
              <textarea
                id="context"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder={mode === 'normal'
                  ? `Describe your decision situation in detail, such as:\n• What specific decision are you facing?\n• Why do you need to make this choice?\n• What factors are you considering?\n• What are your concerns or expectations?\n• Any relevant background information...`
                  : mode === 'elimination'
                  ? `Describe your situation and what you want to know about the remaining options.\n• What are your goals?\n• What are your concerns about the remaining choices?\n• Any other background information...`
                  : `Describe your situation and what you want to know about the accumulated results.\n• What are your goals?\n• What are your concerns about the statistics?\n• Any other background information...`}
                className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                required
              />
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!context.trim()}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
              >
                Start AI Analysis
              </button>
            </div>
          </form>
        </div>
      </div>

      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        remainingUsage={getRemainingUsage()}
      />
    </div>
  )
} 